import React from "react";
import { Coins, Stack, Cube, Hash } from "@phosphor-icons/react";
import { Badge, CopyBtn } from "@/components/primitives";
import { cn, formatLovelace, truncMid } from "@/lib/format";

export const PoolStatRow = ({ icon: Icon, label, value, sub, big }) => (
  <div className="py-4 border-b border-primary/10 last:border-b-0">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono mb-2">
      {Icon ? (
        <span className="w-6 h-6 rounded-md border border-primary/25 bg-primary/[0.06] flex items-center justify-center">
          <Icon size={13} className="text-primary" weight="bold" />
        </span>
      ) : null}
      {label}
    </div>
    <div className={cn("font-mono tabnum text-foreground break-all", big ? "text-[18px]" : "text-[15px]")}>{value}</div>
    {sub ? <div className="text-[11px] text-muted-foreground/70 mono mt-1">{sub}</div> : null}
  </div>
);

// ---------- Pools mode summary panel ----------
export const PoolSummaryPanel = ({ data, amountMode }) => {
  if (!data) return null;
  const marginPct =
    data.margin_cost === null || data.margin_cost === undefined
      ? "—"
      : `${(Number(data.margin_cost) * 100).toFixed(2)}%`;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow px-5 py-3" data-testid="pool-summary-panel">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
      {(data.ticker || data.name) ? (
        <div className="pt-4 pb-1" data-testid="pool-ticker">
          <div className="flex items-center gap-2 flex-wrap">
            {data.ticker ? (
              <span className="font-heading text-[18px] text-primary text-glow">[{data.ticker}]</span>
            ) : null}
            {data.name ? <span className="text-[14px] text-foreground/90 truncate">{data.name}</span> : null}
          </div>
        </div>
      ) : null}
      <PoolStatRow icon={Coins} label="Live Stake" big
        value={formatLovelace(data.live_stake, amountMode)}
        sub={`${(data.live_delegators ?? 0).toLocaleString("en-US")} delegators`} />
      <PoolStatRow icon={Stack} label="Active Stake" big
        value={formatLovelace(data.active_stake, amountMode)}
        sub={data.active_epoch != null ? `epoch ${data.active_epoch}` : null} />
      <PoolStatRow icon={Coins} label="Pledge"
        value={formatLovelace(data.pledge, amountMode)} />
      <PoolStatRow icon={Coins} label="Margin"
        value={marginPct}
        sub={data.fixed_cost != null ? `fixed cost ${formatLovelace(data.fixed_cost, amountMode)}` : null} />
      <PoolStatRow icon={Cube} label="Blocks (current epoch)"
        value={(data.blocks_current_epoch ?? 0).toLocaleString("en-US")} />
      <PoolStatRow label="Status"
        value={data.retired ? <Badge tone="warn">retired</Badge> : <Badge tone="success">active</Badge>}
        sub={data.registration_epoch != null ? `registered epoch ${data.registration_epoch}` : null} />
      <PoolStatRow icon={Hash} label="Hex"
        value={data.hex ? (
          <span className="inline-flex items-center gap-2">
            {truncMid(data.hex, 10, 8)}
            <CopyBtn value={data.hex} label="hex" />
          </span>
        ) : "—"} />
    </div>
  );
};
